import React from 'react';
import { Card } from './Card';

export interface StatsCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    type: 'increase' | 'decrease';
  };
  icon?: React.ReactNode;
  trend?: {
    data: number[];
    color?: string;
  };
  subtitle?: string;
}

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  change,
  icon,
  trend,
  subtitle,
}) => {
  const formatChange = (value: number) => {
    return Math.abs(value).toFixed(1);
  };

  return (
    <Card variant="elevated" padding="md">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <div className="flex items-baseline space-x-2">
            <h3 className="text-3xl font-bold text-gray-900">{value}</h3>
            {change && (
              <span
                className={`inline-flex items-center text-sm font-medium ${
                  change.type === 'increase' ? 'text-success-600' : 'text-danger-600'
                }`}
              >
                {change.type === 'increase' ? (
                  <svg className="w-4 h-4 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
                {formatChange(change.value)}%
              </span>
            )}
          </div>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        {icon && (
          <div className="flex-shrink-0 ml-4">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
              {icon}
            </div>
          </div>
        )}
      </div>
      {trend && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Last 7 days</span>
            <svg className="h-8 w-full" viewBox="0 0 100 30" preserveAspectRatio="none">
              <polyline
                fill="none"
                stroke={trend.color || 'currentColor'}
                strokeWidth="2"
                points={trend.data
                  .map((value, i) => `${(i / (trend.data.length - 1)) * 100},${30 - value}`)
                  .join(' ')}
                className="text-primary-500"
              />
            </svg>
          </div>
        </div>
      )}
    </Card>
  );
};
